import { type ReactNode } from "react";

import { BTStatusBadge, type TBTStatusBadgeDisplayTypes } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BdsButtonGroup, BdsText } from "@buildertrend/components";

import { type BuilderInfo } from "helpers/AppProvider.types";

import { BTItemHeader } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader";
import { type TBTItemHeaderIcon } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.types";
import {
    EntityShareButtonBDS,
    type ISharingConfig,
} from "commonComponents/entity/sharing/EntityShareButton/EntityShareButton";

import "./SelectionOptionHeader.less";

import { useSelectionOptionHeaderActions } from "./SelectionOptionHeaderActions";

export interface ISelectionOptionHeaderProps {
    disabled: boolean;
    isParsingProductUrl: boolean;
    hasDeletePermission: boolean;
    hasUpdatePermission: boolean;
    isCreating: boolean;
    isEditing: boolean;
    isModal?: boolean;
    isExistingEntity: boolean;
    jobName: string;
    onBack: () => void;
    onCancel: () => void;
    onDelete: () => void;
    onEdit: () => void;
    onSave: () => void;
    onSaveAndClose: () => void;
    onSaveAndNew: () => void;
    statusMessage: string;
    statusTestId: string;
    statusType: TBTStatusBadgeDisplayTypes;
    title: string;
    builderInfo: BuilderInfo;
    status?: number;
    onApprove?: () => void;
    onDecline?: () => void;
    onReset?: () => void;
    onSend?: () => void;
    onCopy: () => void;
    canCopy: boolean;
    canSaveToCatalog: boolean;
    onSaveToCatalog: () => void;
    isSavingToCatalog: boolean;
    canCreatePurchaseOrder?: boolean;
    canAddToExistingPurchaseOrder?: boolean;
    onOpenPurchaseOrderLinkingWizard: (toExisting: boolean) => void;
    sharing?: ISharingConfig;
    /** Extra action buttons rendered in the icon toolbar alongside Share/Info (e.g. the comments button). */
    additionalActions?: ReactNode;
    /** When set, Send/Approve/Decline are disabled and show this as a hover tooltip. */
    coBlockMessage?: string | null;
}

export function SelectionOptionHeader(props: Readonly<ISelectionOptionHeaderProps>) {
    const { primaryAction, secondaryActions, iconActions } = useSelectionOptionHeaderActions({
        disabled: props.disabled,
        isParsingProductUrl: props.isParsingProductUrl,
        hasDeletePermission: props.hasDeletePermission,
        hasUpdatePermission: props.hasUpdatePermission,
        isCreating: props.isCreating,
        isEditing: props.isEditing,
        isExistingEntity: props.isExistingEntity,
        isModal: props.isModal,
        status: props.status,
        onCancel: props.onCancel,
        onDelete: props.onDelete,
        onEdit: props.onEdit,
        onSave: props.onSave,
        onSaveAndClose: props.onSaveAndClose,
        onSaveAndNew: props.onSaveAndNew,
        onApprove: props.onApprove,
        onDecline: props.onDecline,
        onReset: props.onReset,
        onSend: props.onSend,
        onCopy: props.onCopy,
        canCopy: props.canCopy,
        canSaveToCatalog: props.canSaveToCatalog,
        onSaveToCatalog: props.onSaveToCatalog,
        isSavingToCatalog: props.isSavingToCatalog,
        canCreatePurchaseOrder: props.canCreatePurchaseOrder,
        canAddToExistingPurchaseOrder: props.canAddToExistingPurchaseOrder,
        openPurchaseOrderLinkingWizard: props.onOpenPurchaseOrderLinkingWizard,
        coBlockMessage: props.coBlockMessage,
    });

    // Ancillary actions (Share, comments/activity) only show for a saved option that
    // has a status — matching the previous slotTop gate.
    const showMetadataActions = props.isExistingEntity && props.status != null;

    // Mount child popovers on the body and bound them to the modal so they avoid overflow
    // clipping and flip at the modal edge instead of the window edge. BdsButtonGroup overwrites
    // each child's root/boundary with its own, so these must be set on the group here.
    const tooltipRoot = props.isModal ? "body" : undefined;
    const tooltipBoundary = props.isModal ? ".ant-modal" : undefined;

    const metadataIconActions: TBTItemHeaderIcon[] = [];
    if (showMetadataActions) {
        metadataIconActions.push({
            type: "customComponent",
            id: "selectionOptionShareSlotTop",
            customComponent: (
                <EntityShareButtonBDS id="selectionOptionShareSlotTop" sharing={props.sharing} />
            ),
            pinned: true,
        });
        if (props.additionalActions) {
            metadataIconActions.push({
                type: "customComponent",
                id: "selectionOptionAdditionalActions",
                // additionalActions may contain a BdsRollupButton (e.g. the comments
                // button), which reads BdsButtonGroup context — wrap it so that context
                // exists outside the old slotTop BdsButtonGroup.
                customComponent: (
                    <BdsButtonGroup
                        id="selectionOptionAncillaryActions"
                        testid="selectionOptionAncillaryActions"
                        root={tooltipRoot}
                        boundary={tooltipBoundary}
                    >
                        {props.additionalActions}
                    </BdsButtonGroup>
                ),
                pinned: true,
            });
        }
    }

    // In a modal/panel the item is dismissed via a left-anchored close (X) that takes
    // the back arrow's place; on the full-page route it's a back arrow. The two are
    // mutually exclusive, and BTItemHeader suppresses both inside a webview.
    return (
        <BTItemHeader
            ariaLabel="Selection Option"
            testid="selectionOptionHeader"
            className={cn("selection-option-header", {
                "selection-option-header--modal": props.isModal,
            })}
        >
            <BTItemHeader.ActionButtons
                testid="selectionOptionHeader"
                backLinkOnClick={props.isModal ? undefined : props.onBack}
                closeOnClick={props.isModal ? props.onBack : undefined}
                primaryAction={primaryAction}
                secondaryActions={secondaryActions.length > 0 ? secondaryActions : undefined}
                iconActions={[...metadataIconActions, ...iconActions]}
            />
            <div className="selection-option-header-title-row">
                <BdsText
                    size="distinct-lg"
                    tag="h1"
                    testid="selectionOptionHeaderTitle"
                    text={props.title}
                />
                {props.jobName && (
                    <BdsText
                        size="normal-md"
                        tag="span"
                        testid="selectionOptionHeaderJobName"
                        text={props.jobName}
                    />
                )}
                {props.isExistingEntity && props.statusMessage && (
                    <BTStatusBadge
                        testid={props.statusTestId}
                        displayType={props.statusType}
                        text={props.statusMessage}
                        textOnly
                    />
                )}
            </div>
        </BTItemHeader>
    );
}

export default SelectionOptionHeader;
